import {
  AggregationGroupBy,
  WorkpackTypeFilterOption,
  WorkpackWbsFilterOption,
  useGetUserPreferencesQuery,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useEffect, useMemo } from 'react';
import { kpiFilterSchema } from '../schemas';
import { useSetAtom } from 'jotai';
import * as kpiState from '../state';
import { RESET } from 'jotai/utils';

export const useInitializeKpiFiltersEffect = () => {
  const router = useRouter();
  const { platformId } = router.query;
  const { data, loading, error } = useGetUserPreferencesQuery({
    skip: typeof platformId !== 'string',
  });

  const setSelectedDeploymentIds = useSetAtom(kpiState.selectedDeploymentIds);
  const setSelectedDateAggregation = useSetAtom(kpiState.selectedDateAggregation);
  const setSelectedStartDate = useSetAtom(kpiState.selectedStartDate);
  const setSelectedEndDate = useSetAtom(kpiState.selectedEndDate);
  const setVisibleColumnKeys = useSetAtom(kpiState.visibleColumnKeys);
  const setSelectedWorkpackTypes = useSetAtom(kpiState.selectedWorkpackTypes);
  const setSelectedWbsFilter = useSetAtom(kpiState.selectedWbsFilter);

  const currentPlatformPreferences = useMemo(() => {
    if (loading) return;
    if (error) {
      console.error(error);
      return;
    }
    if (!data?.getUserPreferences?.platform) return;
    if (typeof platformId !== 'string') return;

    const preferences = kpiFilterSchema.safeParse(data.getUserPreferences.platform?.[platformId]);
    if (!preferences.success) {
      console.error(preferences.error);
      return;
    }

    return preferences.data;
  }, [data?.getUserPreferences?.platform, error, loading, platformId]);

  useEffect(() => {
    const filters = currentPlatformPreferences?.filters;
    const columns = currentPlatformPreferences?.columns;

    if (filters?.kpi_selected_deployments) {
      setSelectedDeploymentIds(filters.kpi_selected_deployments);
    }
    if (filters?.kpi_aggregation_filter) {
      setSelectedDateAggregation(filters.kpi_aggregation_filter as AggregationGroupBy);
    }
    if (filters?.kpi_date_range) {
      const { from, to } = filters.kpi_date_range;

      if (from !== null && from !== undefined) {
        const startDate = new Date(from);
        if (!Number.isNaN(startDate.getTime())) {
          setSelectedStartDate(startDate);
        } else {
          setSelectedStartDate(undefined);
        }
      } else {
        setSelectedStartDate(undefined);
      }

      if (to !== null && to !== undefined) {
        const endDate = new Date(to);
        if (!Number.isNaN(endDate.getTime())) {
          setSelectedEndDate(endDate);
        } else {
          setSelectedEndDate(undefined);
        }
      } else {
        setSelectedEndDate(undefined);
      }
    } else {
      // If no date range filter exists, clear both dates
      setSelectedStartDate(undefined);
      setSelectedEndDate(undefined);
    }
    if (filters?.kpi_selected_workpack_types) {
      setSelectedWorkpackTypes(filters.kpi_selected_workpack_types as WorkpackTypeFilterOption[]);
    }

    if (columns?.kpi_table) {
      setVisibleColumnKeys(columns.kpi_table);
    }

    if (filters?.kpi_selected_wbs) {
      setSelectedWbsFilter(filters.kpi_selected_wbs as WorkpackWbsFilterOption);
    }

    // Reset all filters on unmount
    // so they dont persist when user navigates to another platform page
    return () => {
      setSelectedDeploymentIds(RESET);
      setSelectedDateAggregation(RESET);
      setSelectedStartDate(RESET);
      setSelectedEndDate(RESET);
      setVisibleColumnKeys(RESET);
      setSelectedWorkpackTypes(RESET);
      setSelectedWbsFilter(RESET);
    };
  }, [
    currentPlatformPreferences?.filters,
    setSelectedDeploymentIds,
    setSelectedDateAggregation,
    setSelectedStartDate,
    setSelectedEndDate,
    setVisibleColumnKeys,
    setSelectedWorkpackTypes,
    currentPlatformPreferences?.columns,
    setSelectedWbsFilter,
  ]);
};
